import { useState } from "react";
import { useNavigate, Navigate, Link } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { fmtError } from "@/lib/api";
import { Clock, MapPin, BarChart3 } from "lucide-react";
import Brand from "@/components/Brand";
import usePageMeta from "@/hooks/usePageMeta";

export default function Login() {
  const { user, login } = useAuth();
  const nav = useNavigate();
  const [identifier, setIdentifier] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  usePageMeta({
    title: "Sign in — HRBo",
    description: "Sign in to your HRBo workspace. Admins use email, employees use their phone number. Punch in, view payroll and approve requests in one place.",
    canonical: "https://payroll-hub-265.emergent.host/login",
  });

  if (user) return <Navigate to="/app" replace />;

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const u = await login(identifier, password);
      toast.success(`Welcome, ${u.name}`);
      nav("/app");
    } catch (err) {
      toast.error(fmtError(err.response?.data?.detail) || "Login failed");
    } finally {
      setLoading(false);
    }
  };

  const fillDemo = null; void fillDemo;

  return (
    <div className="min-h-screen grid grid-cols-1 lg:grid-cols-5 bg-white">
      <div className="lg:col-span-3 hidden lg:flex flex-col justify-between p-12 bg-slate-950 text-white">
        <Brand size="md" to={null} dark testid="login-brand" />
        <div className="space-y-8">
          <h1 className="text-5xl xl:text-6xl tracking-tighter font-bold leading-[0.95]" style={{ fontFamily: "Outfit, sans-serif" }}>
            Seamless<br />Attendance.<br /><span className="text-slate-400">Precise Payroll.</span>
          </h1>
          <p className="text-slate-300 max-w-md leading-relaxed">
            Geo-fenced punch-ins, automated payroll, threaded task chats and SaaS-grade
            tenant isolation — built for modern HR teams.
          </p>
          <div className="grid grid-cols-3 gap-4 max-w-md">
            <div className="border border-slate-800 p-4"><Clock className="w-5 h-5 mb-2" strokeWidth={1.5} /><div className="text-xs uppercase tracking-widest text-slate-400">Punch</div></div>
            <div className="border border-slate-800 p-4"><MapPin className="w-5 h-5 mb-2" strokeWidth={1.5} /><div className="text-xs uppercase tracking-widest text-slate-400">Geo-fence</div></div>
            <div className="border border-slate-800 p-4"><BarChart3 className="w-5 h-5 mb-2" strokeWidth={1.5} /><div className="text-xs uppercase tracking-widest text-slate-400">Reports</div></div>
          </div>
        </div>
        <div className="text-xs text-slate-500 uppercase tracking-[0.25em]">HRBo · 2026</div>
      </div>

      <div className="lg:col-span-2 flex items-center justify-center p-6 sm:p-12">
        <form onSubmit={submit} className="w-full max-w-sm space-y-6" data-testid="login-form">
          <div>
            <div className="text-xs uppercase tracking-[0.25em] text-slate-500 mb-2">Sign in</div>
            <h2 className="text-3xl font-semibold tracking-tight text-slate-950" style={{ fontFamily: "Outfit, sans-serif" }}>Welcome back</h2>
            <p className="text-sm text-slate-500 mt-1">Admins use email · Employees use phone</p>
          </div>

          <div className="space-y-3">
            <div>
              <Label className="text-xs uppercase tracking-widest text-slate-500">Email or phone</Label>
              <Input
                type="text" required value={identifier} onChange={(e) => setIdentifier(e.target.value)}
                placeholder="you@company.com or +1 555-555-1212" data-testid="login-identifier-input"
                className="mt-1.5 rounded-sm"
                autoComplete="username"
              />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-widest text-slate-500">Password</Label>
              <Input
                type="password" required value={password} onChange={(e) => setPassword(e.target.value)}
                data-testid="login-password-input"
                className="mt-1.5 rounded-sm"
                autoComplete="current-password"
              />
            </div>
          </div>

          <Button type="submit" disabled={loading} data-testid="login-submit-btn"
                  className="w-full bg-slate-950 hover:bg-slate-800 rounded-sm h-11">
            {loading ? "Signing in..." : "Sign in"}
          </Button>

          <div className="pt-4 border-t border-slate-200 text-center">
            <Link to="/signup" data-testid="login-to-signup-link" className="text-sm text-slate-900 hover:text-slate-950 font-medium underline underline-offset-4">
              New here? Create a company tenant →
            </Link>
          </div>
        </form>
      </div>
    </div>
  );
}
